import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import { getPasskeyErrorMessage } from '../utils/passkeyErrors';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [referralInfo, setReferralInfo] = useState<{ code: string; link: string; points: number } | null>(null);
  const [totalSavings, setTotalSavings] = useState<number>(0);
  const [savingsPercent, setSavingsPercent] = useState<number>(0);
  const [totalSpend, setTotalSpend] = useState<number>(0);
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [lastOrderAt, setLastOrderAt] = useState<string | null>(null);
  const [plans, setPlans] = useState<
    Array<{
      id: string;
      name: string;
      minimumDeposit: number;
      percentageDiscount: number;
      cashbackEnabled: boolean;
      cashbackPercentage: number;
      cashbackTiming: 'on_topup' | 'on_spend';
      priceMatching: boolean;
      pointsConversionRate: number;
      freeDelivery: boolean;
    }>
  >([]);
  const [currentPlan, setCurrentPlan] = useState<{
    name: string;
    percentageDiscount: number;
    cashbackEnabled: boolean;
    cashbackPercentage: number;
    cashbackTiming: 'on_topup' | 'on_spend';
    freeDelivery: boolean;
    priceMatching: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  const [passkeyAdding, setPasskeyAdding] = useState(false);
  const [passkeyRemovingId, setPasskeyRemovingId] = useState<string | null>(null);
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);
  const [passkeys, setPasskeys] = useState<Array<{ id: string; createdAt: string }>>([]);
  const { showSuccess, showError } = useSnackbar();

  const fetchPasskeyStatus = async () => {
    try {
      const response = await api.get('/users/me/passkeys');
      setHasPasskey(response.data.hasPasskey === true);
      setPasskeys(response.data.passkeys || []);
    } catch {
      setHasPasskey(false);
      setPasskeys([]);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([
        fetchBalance(),
        fetchReferralInfo(),
        fetchOrders(),
        fetchPlan(),
        fetchPlans(),
        fetchPasskeyStatus(),
      ]);
      setLoading(false);
    };
    load();
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await api.get('/wallet/balance');
      setBalance(response.data.balance);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchReferralInfo = async () => {
    try {
      const response = await api.get('/users/me/referral');
      setReferralInfo({
        code: response.data.referralCode,
        link: response.data.referralLink,
        points: response.data.referralPoints,
      });
    } catch (error) {
      console.error('Error fetching referral info:', error);
    }
  };

  const handleAddPasskey = async () => {
    const hadPasskeysAlready = passkeys.length > 0;
    setPasskeyAdding(true);
    try {
      const optsRes = await api.post('/auth/passkey/register/options');
      const { options, passkeyToken } = optsRes.data;
      const response = await startRegistration({ optionsJSON: options });
      await api.post('/auth/passkey/register/verify', { passkeyToken, response });
      await fetchPasskeyStatus();
      showSuccess(
        hadPasskeysAlready
          ? 'Passkey added for this device. You can add more on other devices.'
          : 'Passkey added. You can use it to sign in on this device.'
      );
    } catch (err: any) {
      const msg = err.response?.data?.error || getPasskeyErrorMessage(err, 'register');
      showError(msg);
    } finally {
      setPasskeyAdding(false);
    }
  };

  const handleRemovePasskey = async (id: string) => {
    setPasskeyRemovingId(id);
    try {
      await api.delete(`/users/me/passkeys/${id}`);
      await fetchPasskeyStatus();
      showSuccess('Passkey removed.');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to remove passkey');
    } finally {
      setPasskeyRemovingId(null);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      const orders = response.data.orders || [];
      const percent = response.data.membershipDiscountPercent || 0;
      const total = orders.reduce((sum: number, order: { total: number }) => {
        if (!percent || !order.total) return sum;
        const original = order.total / (1 - percent / 100);
        return sum + (original - order.total);
      }, 0);
      setTotalSavings(total);
      setSavingsPercent(percent);
      setTotalOrders(orders.length);
      const spend = orders.reduce((sum: number, order: { total: number }) => sum + (order.total || 0), 0);
      setTotalSpend(spend);
      const lastOrder = orders
        .map((order: { createdAt: string }) => order.createdAt)
        .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime())[0];
      setLastOrderAt(lastOrder || null);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchPlan = async () => {
    try {
      const response = await api.get('/users/me/plan');
      setCurrentPlan(response.data?.plan || null);
    } catch (error) {
      setCurrentPlan(null);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/membership-plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching membership plans:', error);
    }
  };

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.minimumDeposit - b.minimumDeposit),
    [plans]
  );
  const handleClosePlanDetails = () => {
    setShowPlanDetails(false);
    setShowPlanComparison(false);
  };

  const planBenefits = useMemo(() => {
    if (!currentPlan) return 'No plan benefits available.';
    const cashbackLabel = currentPlan.cashbackEnabled
      ? `${currentPlan.cashbackPercentage}% cashback (${currentPlan.cashbackTiming === 'on_spend' ? 'on spend' : 'on top-up'})`
      : 'No cashback';
    return `Discount ${currentPlan.percentageDiscount}%, ${cashbackLabel}, ${
      currentPlan.freeDelivery ? 'free delivery' : 'delivery fees apply'
    }, ${currentPlan.priceMatching ? 'price matching' : 'no price matching'}.`;
  }, [currentPlan]);

  return (
    <div className="container dashboard-container">
      <div className="dashboard-header">
        <h1>Good to see you</h1>
        <p>Here’s a quick snapshot of your membership activity.</p>
      </div>

      <div className="dashboard-metrics">
        <div className="metric-card metric-balance">
          <div className="metric-label">Wallet balance</div>
          <div className="metric-value">
            {loading ? <span className="metric-skeleton" /> : `$${balance?.toFixed(2) || '0.00'}`}
          </div>
          {balance !== null && balance < 100 && (
            <div className="metric-sub">Low balance. Top up to keep shopping.</div>
          )}
          <Link to="/wallet" className="btn btn-primary">
            Top Up Wallet
          </Link>
        </div>

        <div className="metric-card">
          <div className="metric-label">Current plan</div>
          <div className="metric-value">
            {loading ? <span className="metric-skeleton" /> : currentPlan?.name || 'No plan'}
          </div>
          <div className="metric-sub metric-plan-sub">
            <button
              type="button"
              className="plan-info-icon"
              title={planBenefits}
              aria-label="View current plan benefits"
              onClick={() => setShowPlanDetails(true)}
            >
              i
            </button>
            Plan benefits
          </div>
          <div className="metric-actions">
            <Link to="/plans" className="btn btn-secondary">
              Compare plans
            </Link>
            <div className="metric-note">Reach out to admin to migrate plans.</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Total spend</div>
          <div className="metric-value">
            {loading ? <span className="metric-skeleton" /> : `$${totalSpend.toFixed(2)}`}
          </div>
          <div className="metric-sub">
            {loading ? 'Loading orders...' : `Across ${totalOrders} orders`}
          </div>
          <Link to="/orders" className="btn btn-secondary">
            View Orders
          </Link>
        </div>

        <div className="metric-card">
          <div className="metric-label">Member savings</div>
          <div className="metric-value">
            {loading ? <span className="metric-skeleton" /> : `$${totalSavings.toFixed(2)}`}
          </div>
          <div className="metric-sub">
            {savingsPercent > 0 ? `${savingsPercent}% member discount` : 'Member pricing applied'}
          </div>
          <Link to="/products" className="btn btn-secondary">
            Shop & Save
          </Link>
        </div>

        <div className="metric-card">
          <div className="metric-label">Last order</div>
          <div className="metric-value">
            {loading ? (
              <span className="metric-skeleton" />
            ) : lastOrderAt ? (
              new Date(lastOrderAt).toLocaleDateString()
            ) : (
              '—'
            )}
          </div>
          <div className="metric-sub">See your full history anytime.</div>
          <Link to="/orders" className="btn btn-secondary">
            Order History
          </Link>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="action-panel">
          <h2>Quick actions</h2>
          <div className="action-grid">
            <Link to="/products" className="action-card">
              <div className="action-icon">🛍️</div>
              <div>
                <h3>Shop products</h3>
                <p>Browse fresh groceries and member deals.</p>
              </div>
            </Link>
            <Link to="/cart" className="action-card">
              <div className="action-icon">🛒</div>
              <div>
                <h3>Go to cart</h3>
                <p>Review your items and checkout.</p>
              </div>
            </Link>
            <Link to="/messages" className="action-card">
              <div className="action-icon">💬</div>
              <div>
                <h3>Support messages</h3>
                <p>Continue your conversation with support.</p>
              </div>
            </Link>
          </div>
        </div>

        <div className="dashboard-card referral-card">
          <div className="card-icon">🎁</div>
          <h2>Refer & Earn</h2>
          <p>Invite friends and earn points when they sign up.</p>
          <div className="referral-info">
            <div className="referral-row">
              <span>Your Code:</span>
              <span className="referral-code">{referralInfo?.code || '—'}</span>
            </div>
            <div className="referral-row">
              <span>Points:</span>
              <span className="referral-points">{referralInfo?.points ?? 0}</span>
            </div>
            <div className="referral-link">
              <input
                type="text"
                readOnly
                value={referralInfo?.link || ''}
                placeholder="Referral link will appear here"
              />
              <button
                className="btn btn-secondary"
                onClick={() => {
                  if (!referralInfo?.link) return;
                  navigator.clipboard
                    .writeText(referralInfo.link)
                    .then(() => showSuccess('Referral link copied'))
                    .catch(() => showError('Unable to copy referral link'));
                }}
                disabled={!referralInfo?.link}
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-icon">🔐</div>
          <h2>Security</h2>
          {hasPasskey === true && (
            <>
              <p className="passkey-status">
                <span className="passkey-badge" aria-hidden="true">✓</span>{' '}
                {passkeys.length === 1
                  ? 'Passkey enabled — sign-in uses your passkey after your password.'
                  : `${passkeys.length} passkeys — sign in with any registered device.`}
              </p>
              {passkeys.length > 0 && (
                <ul className="passkey-list" aria-label="Your passkeys">
                  {passkeys.map((pk) => (
                    <li key={pk.id} className="passkey-list-item">
                      <span className="passkey-list-label">
                        Passkey · added {new Date(pk.createdAt).toLocaleDateString()}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary passkey-remove-btn"
                        onClick={() => handleRemovePasskey(pk.id)}
                        disabled={passkeyRemovingId === pk.id}
                        aria-label={`Remove passkey added ${new Date(pk.createdAt).toLocaleDateString()}`}
                      >
                        {passkeyRemovingId === pk.id ? 'Removing…' : 'Remove'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {hasPasskey !== true && (
            <p>Add a passkey (Face ID, Touch ID, or device PIN) for two-factor sign-in.</p>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleAddPasskey}
            disabled={passkeyAdding}
          >
            {passkeyAdding ? 'Adding passkey…' : hasPasskey ? 'Add passkey for another device' : 'Add passkey'}
          </button>
        </div>
      </div>
      {showPlanDetails && (
        <div className="plan-modal-overlay" role="dialog" aria-modal="true">
          <div className="plan-modal">
            <div className="plan-modal-header">
              <h3>{currentPlan?.name || 'Plan benefits'}</h3>
              <button
                type="button"
                className="plan-modal-close"
                onClick={handleClosePlanDetails}
                aria-label="Close plan details"
              >
                ×
              </button>
            </div>
            <ul className="plan-modal-list">
              <li>Discount: {currentPlan?.percentageDiscount ?? 0}%</li>
              <li>
                Cashback:{' '}
                {currentPlan?.cashbackEnabled
                  ? `${currentPlan.cashbackPercentage}% (${currentPlan.cashbackTiming === 'on_spend' ? 'on spend' : 'on top-up'})`
                  : 'No cashback'}
              </li>
              <li>{currentPlan?.freeDelivery ? 'Free delivery' : 'Delivery fees apply'}</li>
              <li>{currentPlan?.priceMatching ? 'Price matching' : 'No price matching'}</li>
            </ul>
            {sortedPlans.length > 0 && showPlanComparison && (
              <div className="plan-compare-table">
                <div className="plan-compare-row header">
                  <div>Benefit</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.name}</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Minimum deposit</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>${plan.minimumDeposit.toFixed(0)}</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Member discount</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.percentageDiscount}%</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Cashback</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>
                      {plan.cashbackEnabled
                        ? `${plan.cashbackPercentage}% (${plan.cashbackTiming === 'on_spend' ? 'spend' : 'top-up'})`
                        : 'No'}
                    </div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Free delivery</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.freeDelivery ? 'Yes' : 'No'}</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Price matching</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.priceMatching ? 'Yes' : 'No'}</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Points conversion</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.pointsConversionRate}</div>
                  ))}
                </div>
              </div>
            )}
            <div className="plan-modal-actions">
              <Link to="/plans" className="btn btn-secondary" onClick={handleClosePlanDetails}>
                Compare plans
              </Link>
              {sortedPlans.length > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPlanComparison(prev => !prev)}
                >
                  {showPlanComparison ? 'Hide comparison' : 'Compare here'}
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={handleClosePlanDetails}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

